import React, { useRef, useState } from 'react';
import api from '../../services/api';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { Select } from '../ui/Input';
import { TableCode } from '../ui/Table';
import {
  createStepConfirmationFormData,
  createStepGeometryFormData,
  hasStepIdentity,
} from '../../utils/stepImport';
import './ProductStepPreviewModal.css';

const ACCEPTED_STEP_EXTENSION = /\.(step|stp)$/i;
const PREVIEW_STATES = new Set(['NEW', 'NO_OP', 'CONFLICT']);

function formatDimensions(solid) {
  const dimensions = [solid?.comprimento, solid?.largura, solid?.espessura];
  return dimensions.every((value) => Number.isFinite(Number(value)))
    ? `${dimensions.map((value) => Number(value).toLocaleString('pt-BR')).join(' × ')} mm`
    : 'Dimensões não informadas';
}

function solidLabel(solid, index) {
  return solid.nome || `Sólido ${index + 1}`;
}

function ProductStepPreviewModal({ piece, onApplied = () => {}, onClose }) {
  const inputRef = useRef(null);
  const parseInFlightRef = useRef(false);
  const previewInFlightRef = useRef(false);
  const applyInFlightRef = useRef(false);
  const [file, setFile] = useState(null);
  const [solids, setSolids] = useState([]);
  const [selectedSolidIndex, setSelectedSolidIndex] = useState('');
  const [parseState, setParseState] = useState({ status: 'idle', error: null });
  const [previewState, setPreviewState] = useState({ status: 'idle', data: null, error: null });
  const [applicationState, setApplicationState] = useState({ status: 'idle', message: null });

  const requestPreview = async (solid) => {
    if (previewInFlightRef.current) return;
    if (!hasStepIdentity(solid)) {
      setPreviewState({
        status: 'error',
        data: null,
        error: 'O sólido não possui a identidade STEP V1 completa.',
      });
      return;
    }

    previewInFlightRef.current = true;
    setPreviewState({ status: 'loading', data: null, error: null });
    try {
      const response = await api.post('/api/step/importacao/preview', {
        peca_id: piece.id,
        file_sha256: solid.file_sha256,
        raw_solid_ordinal: solid.raw_solid_ordinal,
        identifier_version: solid.identifier_version,
      });
      if (!PREVIEW_STATES.has(response.data?.estado)) {
        throw new Error('Resposta inválida do preview de proveniência STEP.');
      }
      setPreviewState({ status: 'success', data: response.data, error: null });
    } catch (error) {
      console.error('ProductPage: falha no preview de proveniência STEP:', error);
      const detail = error.response?.data?.detail;
      setPreviewState({
        status: 'error',
        data: null,
        error: typeof detail === 'string'
          ? detail
          : error.message || 'Não foi possível consultar a proveniência STEP.',
      });
    } finally {
      previewInFlightRef.current = false;
    }
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    if (!selectedFile || parseInFlightRef.current) return;

    setFile(null);
    setSolids([]);
    setSelectedSolidIndex('');
    setPreviewState({ status: 'idle', data: null, error: null });
    setApplicationState({ status: 'idle', message: null });
    if (!ACCEPTED_STEP_EXTENSION.test(selectedFile.name)) {
      setParseState({ status: 'error', error: 'Selecione um arquivo .step ou .stp válido.' });
      return;
    }

    setFile(selectedFile);
    setParseState({ status: 'loading', error: null });
    parseInFlightRef.current = true;
    try {
      const formData = new FormData();
      formData.append('files', selectedFile);
      const response = await api.post('/api/step/parse-batch', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parsedSolids = response.data?.pecas;
      if (!Array.isArray(parsedSolids)) {
        throw new Error('Resposta inválida ao processar o arquivo STEP.');
      }
      if (parsedSolids.length === 0) {
        const parserError = response.data?.erros?.[0];
        throw new Error(parserError || 'Nenhum sólido elegível foi encontrado no arquivo STEP.');
      }

      setSolids(parsedSolids);
      setParseState({ status: 'success', error: null });
      if (parsedSolids.length === 1) {
        setSelectedSolidIndex(0);
        await requestPreview(parsedSolids[0]);
      }
    } catch (error) {
      console.error('ProductPage: falha ao processar STEP individual:', error);
      const detail = error.response?.data?.detail;
      setParseState({
        status: 'error',
        error: typeof detail === 'string'
          ? detail
          : error.message || 'Não foi possível processar o arquivo STEP.',
      });
    } finally {
      parseInFlightRef.current = false;
    }
  };

  const handleSolidChange = async (event) => {
    const value = event.target.value;
    setSelectedSolidIndex(value === '' ? '' : Number(value));
    setPreviewState({ status: 'idle', data: null, error: null });
    setApplicationState({ status: 'idle', message: null });
    if (value !== '') await requestPreview(solids[Number(value)]);
  };

  const selectedSolid = selectedSolidIndex === '' ? null : solids[selectedSolidIndex];
  const applying = ['saving', 'confirming'].includes(applicationState.status);
  const busy = parseState.status === 'loading' || previewState.status === 'loading' || applying;
  const previewAllowsApplication = previewState.status === 'success'
    && ['NEW', 'NO_OP'].includes(previewState.data?.estado);
  const showApply = previewAllowsApplication
    && selectedSolid
    && file
    && !['success', 'pending', 'conflict'].includes(applicationState.status);
  const canApply = showApply
    && ['idle', 'save-error'].includes(applicationState.status);

  const handleApply = async () => {
    if (!canApply || applyInFlightRef.current) return;

    applyInFlightRef.current = true;
    let geometrySaved = false;
    setApplicationState({ status: 'saving', message: 'Salvando geometria...' });
    try {
      const geometryData = createStepGeometryFormData(
        selectedSolid,
        { requireIdentity: true }
      );
      await api.put(`/pecas/${piece.id}/salvar`, geometryData);
      geometrySaved = true;
      setApplicationState({
        status: 'confirming',
        message: 'Confirmando proveniência...',
      });

      const confirmationData = createStepConfirmationFormData({
        file,
        pecaId: piece.id,
        stepPiece: selectedSolid,
      });
      await api.post(
        '/api/step/importacao/confirmar',
        confirmationData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setApplicationState({
        status: 'success',
        message: 'Geometria salva e proveniência STEP confirmada.',
      });
      onApplied();
    } catch (error) {
      if (!geometrySaved) {
        console.error('ProductPage: falha ao salvar geometria STEP individual:', error);
        const detail = error.response?.data?.detail;
        setApplicationState({
          status: 'save-error',
          message: typeof detail === 'string'
            ? detail
            : 'Não foi possível salvar a geometria. Nenhuma confirmação foi realizada.',
        });
      } else {
        console.error('ProductPage: falha ao confirmar proveniência STEP individual:', error);
        const payload = error.response?.data;
        if (payload?.code === 'STEP_FILE_HASH_MISMATCH') {
          setApplicationState({
            status: 'pending',
            message: 'Geometria salva, mas os bytes do arquivo não correspondem ao SHA-256 analisado. A proveniência permanece pendente.',
          });
        } else if (payload?.code === 'STEP_IDENTITY_CONFLICT') {
          const associatedPiece = payload.peca_associada_id
            ? ` à peça #${payload.peca_associada_id}`
            : ' a outra peça';
          setApplicationState({
            status: 'conflict',
            message: `Geometria salva, mas a identidade foi associada${associatedPiece} durante a confirmação. A proveniência permanece em conflito.`,
          });
        } else {
          const detail = typeof payload?.detail === 'string' ? ` ${payload.detail}` : '';
          setApplicationState({
            status: 'pending',
            message: `Geometria salva, mas a proveniência não foi confirmada.${detail}`,
          });
        }
        onApplied();
      }
    } finally {
      applyInFlightRef.current = false;
    }
  };

  const renderPreview = () => {
    if (previewState.status === 'loading') {
      return <p className="cw-product-step-loading" role="status">Consultando proveniência...</p>;
    }
    if (previewState.status === 'error') {
      return <div className="cw-product-step-alert cw-product-step-alert--error" role="alert">{previewState.error}</div>;
    }
    if (previewState.status !== 'success') return null;

    const { estado, peca_associada_id: associatedPieceId } = previewState.data;
    const content = {
      NEW: {
        variant: 'info',
        text: 'Identidade ainda não associada. A importação poderá ser aplicada posteriormente.',
      },
      NO_OP: {
        variant: 'success',
        text: 'Identidade já associada à peça selecionada. Nenhuma alteração foi realizada.',
      },
      CONFLICT: {
        variant: 'warning',
        text: associatedPieceId
          ? `Identidade já associada à peça #${associatedPieceId}. O conflito não foi alterado.`
          : 'Identidade já associada a outra peça. O conflito não foi alterado.',
      },
    }[estado];

    return (
      <div className={`cw-product-step-result cw-product-step-result--${estado.toLowerCase()}`} aria-live="polite">
        <Badge variant={content.variant} dot>{estado}</Badge>
        <p>{content.text}</p>
      </div>
    );
  };

  const renderApplicationState = () => {
    if (applicationState.status === 'idle') return null;
    if (applying) {
      return <p className="cw-product-step-loading" role="status">{applicationState.message}</p>;
    }
    const variant = applicationState.status === 'success'
      ? 'success'
      : ['pending', 'conflict'].includes(applicationState.status)
        ? 'warning'
        : 'error';
    return (
      <div
        className={`cw-product-step-alert cw-product-step-alert--${variant}`}
        role={applicationState.status === 'success' ? 'status' : 'alert'}
      >
        {applicationState.message}
      </div>
    );
  };

  return (
    <Modal onClose={busy ? undefined : onClose} className="cw-product-step-modal">
      <div className="cw-product-step-header">
        <div>
          <p className="cw-product-step-eyebrow">Peça selecionada</p>
          <h2>Importar STEP</h2>
        </div>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Fechar</Button>
      </div>

      <p className="cw-product-step-intro">
        Nenhum dado será salvo nesta etapa. A geometria só será salva após a ação explícita Aplicar STEP.
      </p>

      <dl className="cw-product-step-context">
        <dt>Peça alvo</dt>
        <dd><TableCode>{piece.codigo || `#${piece.id}`}</TableCode> {piece.nome || ''}</dd>
        {file && <><dt>Arquivo</dt><dd>{file.name}</dd></>}
        {selectedSolid && (
          <>
            <dt>Sólido</dt>
            <dd>{solidLabel(selectedSolid, selectedSolidIndex)} · ordinal bruto {selectedSolid.raw_solid_ordinal}</dd>
            <dt>Dimensões</dt>
            <dd>{formatDimensions(selectedSolid)}</dd>
          </>
        )}
      </dl>

      <div className="cw-product-step-file-control">
        <input
          ref={inputRef}
          className="cw-product-step-file-input"
          type="file"
          accept=".step,.stp,.STEP,.STP"
          onChange={handleFileChange}
          disabled={busy}
          aria-label="Selecionar arquivo STEP"
        />
        <Button variant="secondary" onClick={() => inputRef.current?.click()} disabled={busy}>
          {file ? 'Escolher outro arquivo' : 'Selecionar arquivo STEP'}
        </Button>
      </div>

      {parseState.status === 'loading' && (
        <p className="cw-product-step-loading" role="status">Processando arquivo STEP...</p>
      )}
      {parseState.status === 'error' && (
        <div className="cw-product-step-alert cw-product-step-alert--error" role="alert">{parseState.error}</div>
      )}

      {solids.length > 1 && (
        <div className="cw-product-step-solid-select">
          <label htmlFor="cw-product-step-solid">Sólido encontrado</label>
          <Select
            id="cw-product-step-solid"
            value={selectedSolidIndex}
            onChange={handleSolidChange}
            disabled={busy || !['idle', 'save-error'].includes(applicationState.status)}
          >
            <option value="">Selecione explicitamente um sólido</option>
            {solids.map((solid, index) => (
              <option key={`${solid.raw_solid_ordinal}-${index}`} value={index}>
                {solidLabel(solid, index)} · {formatDimensions(solid)} · ordinal {solid.raw_solid_ordinal}
              </option>
            ))}
          </Select>
        </div>
      )}

      {renderPreview()}
      {renderApplicationState()}

      {showApply && (
        <div className="cw-product-step-actions">
          <Button
            onClick={handleApply}
            loading={applying}
            disabled={!canApply}
          >
            Aplicar STEP
          </Button>
        </div>
      )}
    </Modal>
  );
}

export default ProductStepPreviewModal;
